<template>
  <div v-if="hasContent" class="sp-finder-result-location-actions" part="base">
    <!-- TODO(parity): legacy "Directions" link dropped for the CHRISTUS redesign; restore for cross-client parity. -->
    <div v-if="hasAddressActions" class="sp-finder-result-location-actions__actions">
      <sp-button
        v-if="showMapLink"
        class="sp-finder-result-location-actions__link"
        variant="appearance-link"
        type="button"
        @click="handleShowMap"
        >{{ t("show_on_map") }}</sp-button
      >
      <span v-if="result.distance" class="sp-finder-result-location-actions__distance">{{ distanceLabel }}</span>
    </div>

    <sp-tile
      v-if="showMoreLocationsTile"
      class="sp-finder-result-location-actions__tile sp-finder-result-location-actions__tile--more-locations"
      icon="map-pin-filled"
      layout="row"
      align="center"
      size="small"
      type="body-bold"
      :href="moreLocationsHref"
      :label="moreLocationsLabel"
    ></sp-tile>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinderResult.ce.vue) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const emit = defineEmits(["show-map"]);

const props = defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
  resultConfig: {
    type: Object,
    default: undefined,
  },
});

const isPlaceholderAddress = computed(() => props.result.placeholder_address);
const isVirtualAddress = computed(() => props.result.virtual_address);
const hasPhysicalAddress = computed(() => !isPlaceholderAddress.value && !isVirtualAddress.value);

const showMapLink = computed(() => props.resultConfig?.showMapLink);
// TODO(parity): CHRISTUS collapses the legacy per-count "More Locations" variants into one generic tile; restore descriptive copy for cross-client parity.
const moreLocationsCount = computed(
  () =>
    Number(props.result.other_online_booking_address_count) ||
    Number(props.result.other_accepting_patients_address_count) ||
    Number(props.result.other_address_count) ||
    0,
);
const showMoreLocationsTile = computed(
  () => hasPhysicalAddress.value && moreLocationsCount.value > 0 && Boolean(props.result.url),
);
const hasAddressActions = computed(
  () => hasPhysicalAddress.value && Boolean(props.result.distance || showMapLink.value),
);
const hasContent = computed(() => hasAddressActions.value || showMoreLocationsTile.value);
const distanceLabel = computed(() => t("distance_away", { distance: props.result.distance }));
const moreLocationsLabel = computed(() =>
  t("more_locations", { count: moreLocationsCount.value }, moreLocationsCount.value),
);
const moreLocationsHref = computed(() => `${props.result.url}#locations`);

function handleShowMap(event) {
  event.preventDefault();
  emit("show-map", { resultGuid: props.result.resultGUID });
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-location-actions {
  /* Layout */
  --location-actions-gap: var(--sp-comp-finder-result-location-actions-gap, var(--sp-ref-spacing-6));

  /* Color */
  --location-actions-link-color: var(--sp-comp-finder-result-location-actions-link-color, var(--sp-sys-color-primary));
  --location-actions-distance-color: var(
    --sp-comp-finder-result-location-actions-distance-color,
    var(--sp-ref-palette-text-light)
  );

  /* Typography */
  --location-actions-link-font-weight: var(
    --sp-comp-finder-result-location-actions-link-font-weight,
    var(--sp-sys-font-weight-bold)
  );
  --location-actions-distance-font-size: var(
    --sp-comp-finder-result-location-actions-distance-font-size,
    var(--sp-ref-font-size-small)
  );
  --location-actions-distance-font-style: var(--sp-comp-finder-result-location-actions-distance-font-style, italic);
  --location-actions-distance-font-weight: var(
    --sp-comp-finder-result-location-actions-distance-font-weight,
    var(--sp-sys-font-weight-normal)
  );
  --location-actions-distance-line-height: var(
    --sp-comp-finder-result-location-actions-distance-line-height,
    var(--sp-ref-line-height-small)
  );

  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--location-actions-gap);
  font: var(--sp-sys-typescale-body-short, inherit);

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--location-actions-gap);
  }

  &__link {
    --sp-comp-button-appearance-link-text-color: var(--location-actions-link-color);
    --sp-comp-button-font-weight: var(--location-actions-link-font-weight);
    --sp-comp-button-appearance-link-text-decoration: none;

    &:hover,
    &:focus-visible {
      --sp-comp-button-appearance-link-text-decoration: underline;
    }
  }

  &__distance {
    color: var(--location-actions-distance-color);
    font-size: var(--location-actions-distance-font-size);
    font-style: var(--location-actions-distance-font-style);
    font-weight: var(--location-actions-distance-font-weight);
    line-height: var(--location-actions-distance-line-height);
  }

  &__tile {
    --sp-comp-tile-gap: var(--sp-ref-spacing-4);
    --sp-comp-tile-gap-sm: var(--sp-ref-spacing-4);
  }

  &__tile--more-locations {
    --sp-comp-tile-inline-size: fit-content;
    --sp-comp-tile-max-inline-size: 100%;
    --sp-comp-tile-label-text-decoration-hover: underline;
    --sp-comp-tile-icon-fill: var(--location-actions-link-color);
    --sp-comp-tile-label-color: var(--location-actions-link-color);
  }
}
</style>
